import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { useAuth } from '../../lib/auth-context';
import { authAPI } from '../../lib/api';
import { Camera, Save, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function ProfileSetting() {
  const { user, loadUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    full_name: user?.full_name || '',
    bio: user?.bio || '',
    title: user?.title || '',
    phone: user?.phone || '',
    location: user?.location || '',
    website: user?.website || '',
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5 MB'); return; }
    setUploading(true);
    try {
      const res = await authAPI.uploadAvatar(file);
      localStorage.setItem('user', JSON.stringify(res.data));
      await loadUser();
      toast.success('Profile photo updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleRemoveAvatar = async () => {
    setSaving(true);
    try {
      const res = await authAPI.updateProfile({ avatar_url: '' });
      localStorage.setItem('user', JSON.stringify(res.data));
      await loadUser();
      toast.success('Profile photo removed');
    } catch (err) {
      toast.error('Failed to remove photo');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!form.full_name.trim()) { toast.error('Full name is required'); return; }
    setSaving(true);
    try {
      const res = await authAPI.updateProfile(form);
      localStorage.setItem('user', JSON.stringify(res.data));
      await loadUser();
      toast.success('Profile updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const avatarUrl = user?.avatar_url;

  return (
    <div className="space-y-6">
      <Card className="bg-white border-[#E7E0D6]">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Profile Photo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            <div className="relative group cursor-pointer" onClick={() => !uploading && fileRef.current?.click()}>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
              {avatarUrl ? (
                <img src={avatarUrl} alt="Avatar" className="w-20 h-20 rounded-full object-cover" />
              ) : (
                <div className="w-20 h-20 rounded-full bg-[#B89B5E] flex items-center justify-center text-2xl font-bold text-white">
                  {form.full_name?.charAt(0) || 'U'}
                </div>
              )}
              <div className="absolute inset-0 rounded-full bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                {uploading ? <Loader2 className="w-5 h-5 text-white animate-spin" /> : <Camera className="w-5 h-5 text-white" />}
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-7 h-7 rounded-full bg-[#B89B5E] border-2 border-white flex items-center justify-center">
                {uploading ? <Loader2 className="w-3 h-3 text-white animate-spin" /> : <Camera className="w-3 h-3 text-white" />}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">{form.full_name}</p>
              <p className="text-xs text-[#5B616E] capitalize">{user?.role?.replace(/_/g, ' ')}</p>
              <p className="text-xs text-[#5B616E]">{user?.email}</p>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} className="text-xs h-7">
                  {uploading ? 'Uploading...' : 'Change Photo'}
                </Button>
                {avatarUrl && (
                  <Button variant="ghost" size="sm" onClick={handleRemoveAvatar} disabled={saving} className="text-xs h-7 text-red-500 hover:text-red-600">
                    <Trash2 className="w-3 h-3 mr-1" /> Remove
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E7E0D6]">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Personal Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-[#5B616E]">Full Name</Label>
              <Input value={form.full_name} onChange={e => update('full_name', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-[#5B616E]">Job Title</Label>
              <Input value={form.title} onChange={e => update('title', e.target.value)} placeholder="e.g. Senior Editor" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-[#5B616E]">Bio</Label>
            <Textarea value={form.bio} onChange={e => update('bio', e.target.value)} rows={3} placeholder="Tell us about yourself..." />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-[#5B616E]">Phone</Label>
              <Input value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="+1 (555) 000-0000" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-[#5B616E]">Location</Label>
              <Input value={form.location} onChange={e => update('location', e.target.value)} placeholder="City, Country" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-[#5B616E]">Website</Label>
            <Input value={form.website} onChange={e => update('website', e.target.value)} placeholder="https://..." />
          </div>
          <div className="flex justify-end pt-2">
            <Button onClick={handleSave} disabled={saving} className="bg-[#121316] text-white hover:bg-[#1B1D22]">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {saving ? 'Saving...' : 'Save Profile'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
